import React, { useState, useEffect, useRef } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import CircularLoader from './CircularLoader';
import { getAllItems } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';

const NewArrivals = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [addedProductId, setAddedProductId] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const openProductPage = (product) => {
    if (!product?.id) return;
    const slug = slugify(product?.name || 'product');
    navigate(`/product/${encodeURIComponent(product.id)}/${encodeURIComponent(slug)}`);
  };

  // Normalize API photo field: fix cases like "image/upload/https://..." and accept data URLs
  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed; // base64/data URL
    // If API returned a malformed cloudinary string like 'image/upload/https://...'
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadItems = async () => {
      try {
        if (!cancelled) {
          setIsLoading(true);
        }
        const response = await getAllItems();
        const items = response?.items || response || [];

        const mapped = Array.isArray(items)
          ? items.map((item) => {
              const priceNumber = parseFloat(item.price) || 0;
              const priceText = `KSh ${priceNumber.toLocaleString('en-KE', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`;

              // Normalize photo/image data similarly to admin Products page
              let image = '';
              const candidateSingleRaw = item.photo || item.image;
              const candidateSingle = normalizePhotoValue(candidateSingleRaw);
              const candidateArrayRaw = item.images || [];
              const candidateArray = Array.isArray(candidateArrayRaw)
                ? candidateArrayRaw
                    .map((img) => {
                      if (!img) return '';
                      if (typeof img === 'string') return normalizePhotoValue(img);
                      if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
                      return '';
                    })
                    .filter(Boolean)
                : [];

              if (candidateSingle && typeof candidateSingle === 'string' && candidateSingle.trim() !== '') {
                image = candidateSingle;
              } else if (Array.isArray(candidateArray) && candidateArray.length > 0) {
                const thumbObj = Array.isArray(candidateArrayRaw)
                  ? candidateArrayRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail)
                  : null;
                if (thumbObj && typeof thumbObj.image === 'string') {
                  image = normalizePhotoValue(thumbObj.image);
                } else {
                  image = candidateArray[0] || '';
                }
              }

              return {
                id: item.id,
                name: item.name,
                price: priceNumber,
                priceText,
                image,
                rating: item.average_rating || 0,
                reviews: item.reviews_count || 0,
                description: item.description || '',
                categories: item.categories || [],
                category: item.category || '',
              };
            })
          : [];

        // Sort by id desc (latest added first), then take only 8
        const latestEight = Array.isArray(mapped)
          ? [...mapped]
              .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
              .slice(0, 8)
          : [];

        if (!cancelled) {
          setProducts(latestEight);
        }
      } catch (err) {
        console.error('Failed to load new arrival items:', err);
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddToCart = (product, qty = 1) => {
    for (let i = 0; i < qty; i++) {
      addToCart(product);
    }
    setAddedProductId(product.id);
    setTimeout(() => setAddedProductId(null), 2000);
  };

  const StarRating = ({ rating, reviews, showReviews = false }) => {
    return (
      <div className="flex items-center space-x-1 mb-3">
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              className={`w-5 h-5 ${star <= rating ? 'text-orange-400' : 'text-gray-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        {showReviews && (
          <span className="text-gray-500 text-sm ml-2">({reviews} customer reviews)</span>
        )}
      </div>
    );
  };

  return (
    <div ref={sectionRef} className="py-16 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          {/* First Text - Animate from top-center moving up */}
          <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-wide transform transition-all duration-1000 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-12 opacity-0'
          }`}>
            NEW ARRIVALS
          </h2>
          
          {/* Second Text - Animate from same point moving down */}
          <p className={`text-lg text-gray-600 transform transition-all duration-1000 ease-out delay-200 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            Sitewide Discounts & Savings Of Up To 25%
          </p>
        </div>

        <div className={`max-w-4xl mx-auto mb-10 transform transition-all duration-1000 ease-out delay-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-orange-50 via-white to-green-50 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Buy products or chat with us</div>
                <div className="text-sm text-gray-600">Fast checkout or WhatsApp support for quick ordering.</div>
              </div>

              <div className="flex flex-col xs:flex-row sm:flex-row gap-3">
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 text-sm font-semibold transition-colors"
                >
                  Buy Products
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>

                <a
                  href={`https://wa.me/254714139461?text=${encodeURIComponent('Hello Brand Decor, I would like to order and get more details.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-black hover:bg-gray-900 text-white px-5 py-3 text-sm font-semibold transition-colors"
                >
                  Chat WhatsApp
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {isLoading && products.length === 0 && (
            <div className="col-span-full py-10">
              <CircularLoader label="Loading new arrivals..." size="lg" />
            </div>
          )}

          {!isLoading && products.map((product, index) => (
            <div 
              key={product.id}
              className={`bg-gray-50 rounded-3xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 relative group transform ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
              }`}
              style={{
                transitionDelay: `${400 + index * 100}ms`,
                transitionDuration: '800ms'
              }}
            >
              {/* Product Image */}
              <div className="relative mb-6 overflow-hidden rounded-2xl bg-white p-4">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-xl"
                  loading="lazy"
                  decoding="async"
                />
                
                {/* Quick View Icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button 
                    onClick={() => openProductPage(product)}
                    className="bg-white/90 p-2 rounded-full shadow-md hover:bg-white"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Star Rating (display only on card) */}
              <StarRating 
                rating={product.rating} 
                reviews={product.reviews}
              />

              {/* Product Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {product.name}
              </h3>

              {/* Price */}
              <p className="text-lg font-semibold text-orange-500 mb-4">
                {product.priceText}
              </p>

              {/* Short Description on card */}
              {product.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {product.description}
                </p>
              )}

              {/* Actions: Add to Cart + WhatsApp */}
              <div className="flex flex-col space-y-3 mt-auto">
                <Link
                  to={`/product/${encodeURIComponent(product.id)}/${encodeURIComponent(slugify(product.name))}`}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 px-6 rounded-full font-semibold transition-all duration-300 border border-gray-200 hover:shadow-sm touch-manipulation text-center"
                >
                  More Details
                </Link>
                <button
                  onClick={() => handleAddToCart(product)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-6 rounded-full font-semibold transition-all duration-300 hover:scale-105 touch-manipulation"
                >
                  Add To Cart
                </button>

                {/* WhatsApp Inquiry Button */}
                <a
                  href={`https://wa.me/254714139461?text=${encodeURIComponent(
                    `Hello Brand Decor, I would like to inquire about this product: ${product.name}.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-start bg-black text-white py-2.5 px-3 rounded-full font-semibold transition-all duration-300 hover:bg-gray-900"
                >
                  {/* Left WhatsApp circle */}
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500 text-white mr-2 shadow-sm">
                    <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
                  </span>
                  {/* Text block: 'Order On' small, 'Whatsapp' bold */}
                  <div className="flex flex-col leading-tight">
                    <span className="text-xs text-green-400">Order On</span>
                    <span className="text-sm font-bold">Whatsapp</span>
                  </div>
                </a>
              </div>

              {/* Inline Toast Positioned Near Button */}
              {addedProductId === product.id && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-20 bg-black text-white text-xs md:text-sm px-3 py-2 rounded-md shadow-lg pointer-events-none">
                  Added to cart
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewArrivals;
